// SEO ligero sin dependencias: gestiona el <title>, meta tags (description,
// robots, canonical), Open Graph / Twitter Cards y datos estructurados (JSON-LD)
// por página. Pensado para la capa pública (marketing) de la PWA.
//
// No usamos react-helmet para no añadir dependencias: un hook que escribe en el
// <head> en el efecto de montaje y limpia los nodos que creó al desmontar.

import { useEffect } from 'react';
import { currentLocale, SUPPORTED_LOCALES } from '../i18n/index.js';
import type { Locale } from '../types.js';
import { aggregateOffer } from './planCatalog.js';

/** Mapa de idioma soportado → etiqueta Open Graph `og:locale` (lang_TERRITORY). */
const OG_LOCALE: Record<Locale, string> = {
  es: 'es_ES',
  en: 'en_US',
  zh: 'zh_CN',
  hi: 'hi_IN',
  pt: 'pt_BR',
  fr: 'fr_FR',
  ru: 'ru_RU',
  bn: 'bn_BD',
};

/**
 * Dominio canónico del sitio. DEBE coincidir con el host real servido: el apex
 * `saands.co` hace 301 a `www.saands.co`, así que el canónico es el `www`.
 * Un canónico apuntando a otro host (p. ej. el antiguo `saands.app`, que además
 * redirige aquí) hace que Google reparta/descartar señales de indexación.
 */
export const SITE_URL = 'https://www.saands.co';
/** Nombre de marca. */
export const SITE_NAME = 'saands';
/**
 * Imagen social por defecto (og:image). Formato 1200×630 (ratio 1.91:1) que es
 * el que Open Graph / Twitter `summary_large_image` recortan sin deformar; un
 * ícono cuadrado (pwa-512) se ve recortado y pequeño en los previews.
 */
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

export interface SeoConfig {
  /** Título de la pestaña / SERP. Se le añade el sufijo de marca salvo que sea el home. */
  title: string;
  /** Meta description (120–160 caracteres idealmente). */
  description: string;
  /** Ruta canónica (relativa, p. ej. "/precios"). */
  path?: string;
  /** URL absoluta de la imagen social. */
  image?: string;
  /** Indexar o no (por defecto sí). */
  noindex?: boolean;
  /** Palabras clave (meta keywords; secundario pero inofensivo). */
  keywords?: string[];
  /** Bloques JSON-LD de datos estructurados. */
  jsonLd?: Array<Record<string, unknown>>;
}

/** Crea/actualiza un <meta name=".."> o <meta property=".."> gestionado. */
function upsertMeta(attr: 'name' | 'property', key: string, content: string, managed: Element[]): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute('data-seo-managed', 'true');
    document.head.appendChild(el);
    managed.push(el);
  }
  el.setAttribute('content', content);
}

/** Hook de SEO por página. Escribe el head al montar y restaura al desmontar. */
export function useSeo(config: SeoConfig): void {
  useEffect(() => {
    const managed: Element[] = [];
    const prevTitle = document.title;

    const fullTitle =
      config.path === '/' || config.title.toLowerCase().includes('saands')
        ? config.title
        : `${config.title} · ${SITE_NAME}`;
    document.title = fullTitle;

    const canonical = `${SITE_URL}${config.path ?? ''}`;
    const image = config.image ?? DEFAULT_OG_IMAGE;

    upsertMeta('name', 'description', config.description, managed);
    upsertMeta('name', 'robots', config.noindex ? 'noindex,nofollow' : 'index,follow', managed);
    if (config.keywords?.length) {
      upsertMeta('name', 'keywords', config.keywords.join(', '), managed);
    }

    // Canonical.
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const createdCanonical = !link;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    const prevCanonical = link.getAttribute('href');
    link.setAttribute('href', canonical);

    // hreflang (P-41 / P1-3, ruta (a)): el sitio sirve UNA sola URL por ruta
    // (contenido en español; el idioma se resuelve por preferencia del cliente en
    // la MISMA URL). Declarar 8 alternativas hreflang apuntando todas a esa misma
    // URL es INVÁLIDO (hreflang exige una URL distinta por idioma) y confunde a
    // Google. No existen versiones `/en/…`, `/pt/…` reales. Por eso retiramos las
    // alternativas por idioma y dejamos SOLO `x-default` → la URL canónica, que es
    // la única señal veraz. (La ruta (b) —prerender de versiones por idioma— queda
    // propuesta como seguimiento del owner, no implementada.)
    const xDefault = document.createElement('link');
    xDefault.setAttribute('rel', 'alternate');
    xDefault.setAttribute('hreflang', 'x-default');
    xDefault.setAttribute('href', canonical);
    xDefault.setAttribute('data-seo-managed', 'true');
    document.head.appendChild(xDefault);
    managed.push(xDefault);

    // Mantiene el atributo `lang` del <html> en sincronía con el idioma activo,
    // para crawlers que leen el HTML servido / tecnologías de asistencia.
    const prevLang = document.documentElement.lang;
    document.documentElement.lang = OG_LOCALE[currentLocale()].split('_')[0]!;

    // Open Graph.
    upsertMeta('property', 'og:type', 'website', managed);
    upsertMeta('property', 'og:site_name', SITE_NAME, managed);
    upsertMeta('property', 'og:title', fullTitle, managed);
    upsertMeta('property', 'og:description', config.description, managed);
    upsertMeta('property', 'og:url', canonical, managed);
    upsertMeta('property', 'og:image', image, managed);
    // Dimensiones del og:image: ayudan a los scrapers sociales a reservar el
    // espacio y validar el ratio 1.91:1 sin descargar la imagen primero.
    upsertMeta('property', 'og:image:width', '1200', managed);
    upsertMeta('property', 'og:image:height', '630', managed);
    upsertMeta('property', 'og:locale', OG_LOCALE[currentLocale()], managed);
    // Alternativas de idioma en Open Graph (og:locale:alternate).
    for (const info of SUPPORTED_LOCALES) {
      const tag = OG_LOCALE[info.code];
      if (tag !== OG_LOCALE[currentLocale()]) {
        const el = document.createElement('meta');
        el.setAttribute('property', 'og:locale:alternate');
        el.setAttribute('content', tag);
        el.setAttribute('data-seo-managed', 'true');
        document.head.appendChild(el);
        managed.push(el);
      }
    }

    // Twitter Card.
    upsertMeta('name', 'twitter:card', 'summary_large_image', managed);
    upsertMeta('name', 'twitter:title', fullTitle, managed);
    upsertMeta('name', 'twitter:description', config.description, managed);
    upsertMeta('name', 'twitter:image', image, managed);

    // JSON-LD.
    const scripts: HTMLScriptElement[] = [];
    for (const block of config.jsonLd ?? []) {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo-managed', 'true');
      s.text = JSON.stringify(block);
      document.head.appendChild(s);
      scripts.push(s);
    }

    return () => {
      document.title = prevTitle;
      document.documentElement.lang = prevLang;
      for (const el of managed) el.remove();
      for (const s of scripts) s.remove();
      if (createdCanonical) {
        link?.remove();
      } else if (link && prevCanonical) {
        link.setAttribute('href', prevCanonical);
      }
    };
  }, [config]);
}

/** JSON-LD de la organización (reutilizable en varias páginas). */
export function organizationJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo-saands.png`,
    description:
      'Platform for vinyl collectors: catalog, value, enrich with Discogs and hunt for deals across your collection.',
    sameAs: [] as string[],
  };
}

/** JSON-LD de software/servicio (SaaS). */
export function softwareJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: SITE_NAME,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, iOS, Android',
    description:
      'App for vinyl collectors: add records with a photo, complete each entry automatically, find duplicates, know how much your collection is worth and get deal alerts.',
    // P-41 (P0-2): el rango de precios se deriva del catálogo CANÓNICO (espejo de
    // `/public/plans`), no de un literal: antes decía highPrice "12" (falso); el
    // tope real es 20 (plan Ilimitado). Una sola fuente evita que vuelva a divergir.
    offers: aggregateOffer(),
  };
}

/** JSON-LD de preguntas frecuentes. */
export function faqJsonLd(faqs: Array<{ q: string; a: string }>): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}
